import { Link } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';
import { useTranslation } from 'react-i18next';

import Icon from 'components/icon/Icon';
import { ScrollArea } from 'components/ui/scroll-area';
import { PATH_ROUTES } from 'routes/path';
import { ReportInsight } from '../../types';
import { REPORT_IMAGE_MAPPER } from 'lib/report-recommendations';

interface ReportInsightsProps {
  insight: ReportInsight | undefined;
  scanId: string;
  reportType?: 'ai' | 'lab';
}

export default function ReportInsights({
  insight,
  scanId,
  reportType = 'ai',
}: ReportInsightsProps) {
  const { t } = useTranslation();
  const isSmallDevice = useMediaPredicate('(max-width: 1024px)');

  const nextSteps = insight?.test_and_next_step
    ? Array.isArray(insight?.test_and_next_step)
      ? insight?.test_and_next_step
      : Object.entries(insight.test_and_next_step)?.map(
          ([key, value]) => `${key}: ${value}`
        )
    : [];

  return (
    <ScrollArea className='h-fit sm:h-[calc(100vh-17rem)] lg:h-[calc(100vh-14rem)] xl:h-[calc(100vh-12rem)]'>
      <div className='py-4 space-y-4'>
        <div className='flex flex-col items-center justify-between gap-y-2 lg:flex-row'>
          <div className='flex items-center gap-x-2'>
            <Icon icon='health-plus' size={20} className='mb-2 text-primary' />

            <h1 className='text-lg font-semibold tracking-tight lg:text-2xl text-primary'>
              {t('health_at_a_glance')}
            </h1>

            <Icon icon='health-bottle' size={22} className='text-primary' />
          </div>

          {reportType === 'ai' ? (
            <Link
              to={PATH_ROUTES.FACE_SCAN + '?report_id=' + scanId}
              className='flex items-center font-medium tracking-tight underline text-primary underline-offset-2'
            >
              {t('go_to_corresponding_face_scan')}
            </Link>
          ) : null}
        </div>

        <div className='grid grid-cols-1 xl:grid-cols-[1fr_100px] xl:items-center'>
          <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
            {insight?.attention
              ? Object.entries(insight?.attention).map(([key, value]) => {
                  if (key.toLowerCase() === 'not enough information')
                    return null;

                  return (
                    <div className='h-full' key={key}>
                      <div className='flex items-start px-4 py-3 border h-fit border-rose-500 rounded-3xl gap-x-2'>
                        <Icon
                          icon='health-tablet'
                          size={16}
                          className='mt-2 text-rose-500'
                        />

                        <div className='flex-1'>
                          <h2 className='text-base font-semibold tracking-tight lg:text-lg text-rose-500'>
                            {key}
                          </h2>
                          <p className='text-sm font-medium leading-tight'>
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>

          {isSmallDevice ? null : <Icon icon='alert-frame' size={100} />}
        </div>

        <div className='grid grid-cols-1 gap-y-4 xl:grid-cols-2 gap-x-4'>
          <div className='p-3 border xl:p-4 border-violet-500 rounded-2xl'>
            <div className='flex gap-x-2'>
              {isSmallDevice ? null : (
                <Icon icon='prevention' size={18} className='mt-2' />
              )}
              <h2 className='text-base font-semibold text-blue-400 xl:text-lg'>
                {t('prevention_recommendation')}
              </h2>
            </div>

            <PreventionRecommendation recommentation={insight?.prevention} />
          </div>

          <div className='flex p-3 border border-gray-600 xl:p-4 rounded-2xl'>
            {isSmallDevice ? null : (
              <Icon icon='microscope' size={18} className='mt-2 mr-4' />
            )}
            <div className='flex flex-col flex-1'>
              <div className='flex-1'>
                <h2 className='text-base font-semibold text-gray-600 xl:text-lg'>
                  {t('test_next_steps')}
                </h2>
                <ul className='grid grid-cols-1 pl-4 mt-2 mb-4 space-y-2 text-sm list-disc leading-tighter'>
                  {nextSteps?.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

function PreventionRecommendation({
  recommentation,
}: {
  recommentation: Record<string, string> | undefined;
}) {
  if (!recommentation || !Object.keys(recommentation).length) return null;

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {Object.entries(recommentation).map(([key, value]) => (
        <div key={key} className='flex gap-x-4'>
          <div className='hidden w-16 h-16 shrink-0 sm:block'>
            <img
              src={REPORT_IMAGE_MAPPER[key as keyof typeof REPORT_IMAGE_MAPPER]}
              alt={key}
              className='object-contain w-full h-full'
            />
          </div>

          <div className='flex flex-col'>
            <p className='mb-1 font-semibold leading-none capitalize'>{key}</p>
            <p className='text-sm leading-none'>{value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
